import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Avatar,
  useTheme,
  alpha,
  CircularProgress,
  Grid2 as Grid,
  IconButton,
} from "@mui/material";
import {
  Person as PersonIcon,
  AccessTime as AccessTimeIcon,
  Login as LoginIcon,
  AdminPanelSettings as AdminIcon,
  SupervisorAccount as ManagerIcon,
  School as CoachIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import useAdminLoginStats from "./useAdminLoginStats";

export interface AdminLoginStatsProps {
  comId: string;
}

/**
 * 관리자 로그인 통계 템플릿 컴포넌트
 * @description 도장 관리자들의 로그인 통계를 테이블 형태로 표시합니다.
 * @param comId 도장 ID
 */
export const AdminLoginStats = ({ comId }: AdminLoginStatsProps) => {
  const theme = useTheme();

  // 비즈니스 로직을 Hook으로 분리
  const {
    stats,
    summaryStats,
    isLoading,
    error,
    formatSessionTime,
    formatLastLogin,
    getAdminTypeInfo,
    refreshStats,
  } = useAdminLoginStats({ comId });

  // 관리자 타입별 아이콘 매핑
  const getAdminTypeIcon = (type: string) => {
    switch (type?.toUpperCase()) {
      case "OWNER":
        return <AdminIcon fontSize="small" />;
      case "MANAGER":
        return <ManagerIcon fontSize="small" />;
      case "COACH":
      case "INSTRUCTOR":
        return <CoachIcon fontSize="small" />;
      default:
        return <PersonIcon fontSize="small" />;
    }
  };

  // 에러 상태
  if (error) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight={400}
        gap={2}
      >
        <Typography variant="h6" color="error">
          데이터 로딩 중 오류가 발생했습니다.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {error}
        </Typography>
        <IconButton onClick={refreshStats} color="primary">
          <RefreshIcon />
        </IconButton>
      </Box>
    );
  }

  // 로딩 상태
  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={400}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ py: 2 }}>
      {/* 헤더 */}
      <Box
        sx={{
          mb: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight="600" gutterBottom>
            관리자 로그인 통계
          </Typography>
          <Typography variant="body2" color="text.secondary">
            도장 관리자들의 apps 플랫폼 접속 현황을 확인하세요
          </Typography>
        </Box>
        <IconButton
          onClick={refreshStats}
          color="primary"
          disabled={isLoading}
          sx={{
            backgroundColor: alpha(theme.palette.primary.main, 0.04),
            "&:hover": {
              backgroundColor: alpha(theme.palette.primary.main, 0.08),
            },
          }}
        >
          <RefreshIcon />
        </IconButton>
      </Box>

      {/* 요약 통계 */}
      {summaryStats && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid size={{ xs: 12, md: 3 }}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: "center", py: 2 }}>
                <Typography variant="h4" fontWeight="700" color="primary">
                  {summaryStats.totalAdmins}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  총 관리자 수
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: "center", py: 2 }}>
                <Typography variant="h4" fontWeight="700" color="primary">
                  {summaryStats.activeAdmins}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  활성 관리자
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: "center", py: 2 }}>
                <Typography variant="h4" fontWeight="700" color="primary">
                  {summaryStats.todayLogins}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  오늘 로그인
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: "center", py: 2 }}>
                <Typography variant="h4" fontWeight="700" color="primary">
                  {summaryStats.weeklyLogins}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  주간 로그인
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* 관리자 목록 테이블 */}
      <Card variant="outlined">
        <CardContent sx={{ p: 0 }}>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: alpha(theme.palette.primary.main, 0.04),
                  }}
                >
                  <TableCell>관리자</TableCell>
                  <TableCell>권한</TableCell>
                  <TableCell align="center">로그인 횟수</TableCell>
                  <TableCell align="center">총 세션 시간</TableCell>
                  <TableCell align="center">평균 세션 시간</TableCell>
                  <TableCell>마지막 로그인</TableCell>
                  <TableCell align="center">상태</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {stats && stats.length > 0 ? (
                  stats.map((admin) => {
                    const typeInfo = getAdminTypeInfo(admin.admType);
                    const typeIcon = getAdminTypeIcon(admin.admType);

                    return (
                      <TableRow
                        key={admin.admId}
                        sx={{
                          "&:hover": {
                            backgroundColor: alpha(
                              theme.palette.primary.main,
                              0.02
                            ),
                          },
                        }}
                      >
                        <TableCell>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                            }}
                          >
                            <Avatar
                              sx={{
                                width: 32,
                                height: 32,
                                backgroundColor: alpha(
                                  theme.palette[typeInfo.colorType].main,
                                  0.1
                                ),
                                color: theme.palette[typeInfo.colorType].main,
                              }}
                            >
                              <PersonIcon fontSize="small" />
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight="600">
                                {admin.admNm}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {admin.email}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={typeInfo.label}
                            icon={typeIcon}
                            color={typeInfo.colorType}
                            variant="outlined"
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 0.5,
                            }}
                          >
                            <LoginIcon fontSize="small" color="action" />
                            <Typography variant="body2" fontWeight="600">
                              {admin.loginCount}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2">
                            {formatSessionTime(admin.totalSessionTime)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2">
                            {formatSessionTime(admin.avgSessionTime)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <AccessTimeIcon fontSize="small" color="action" />
                            <Typography variant="body2">
                              {formatLastLogin(admin.lastLoginDttm)}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            size="small"
                            label={admin.isActive ? "활성" : "비활성"}
                            color={admin.isActive ? "success" : "default"}
                            variant={admin.isActive ? "filled" : "outlined"}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        관리자 로그인 통계가 없습니다.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};
